import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ais-ui/ui-core';

import { cn } from '@ais-ui/utils';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  PiCaretLeftLight,
  PiCaretRightLight,
  PiChatCircleText,
  PiDiamond,
  PiFunnel,
  PiMonitorPlay,
  PiNotebook,
  PiPause,
  PiPlay,
  PiPolygon,
  PiStopFill,
} from 'react-icons/pi';
import { ACTIVETAB } from '../../modules/Map/MapPanel/RightSideComponents';
import { useMapPanelContext } from '../../modules/Map/MapPanel/MapPanelContext';
import { useNotificationContext } from '../../modules/Notifications/NotificationContext';
import { usePermissions } from '../../modules/Permissions/PermissionsProvider';
import { TbRefreshAlert } from 'react-icons/tb';
import {
  atonsButtonId,
  eventsButtonId,
  filtersButtonId,
  messagesButtonId,
  replayButtonId,
  reportsButtonId,
  shapesButtonId,
  useSidebarItemAction,
} from './useSidebarItemAction';
import { useLocalization } from '@ais-ui/locale-management';
import { useSession } from '@ais-ui/websockets';
import { noop } from 'lodash';
import { UserDropdown } from './UserDropdown/UserDropdown';
import { useWhitelabel } from '../../configuration/useWhitelabel';
import { useSettings } from '../../modules/Settings/SettingsProvider';
import { useReplayContext } from '../../modules/Replay/ReplayContext';
import { useStartStopPauseReplay } from '../../modules/Replay/usePlayReplay';
import { useViewMode, ViewMode } from '../ViewModeProvider';
import { trackOperation } from '@ais-ui/performance-tracking';

interface SidebarProps {
  id: string;
  className?: string;
}

interface SidebarButtonProps {
  id?: string;
  icon: React.ElementType;
  disabled?: boolean;
  disabledText?: string;
  text: string;
  tooltip: string;
  onClick: () => void;
  isActive: boolean;
  expanded: boolean;
  forceHideTooltip?: boolean;
}

const SidebarButton = forwardRef<HTMLButtonElement, SidebarButtonProps>(
  (
    {
      id,
      icon: Icon,
      disabled,
      disabledText,
      text,
      tooltip,
      onClick,
      isActive,
      expanded,
      forceHideTooltip = false,
    }: SidebarButtonProps,
    ref,
  ) => {
    const tooltipText = useMemo(() => {
      if (disabled) {
        return disabledText;
      } else return tooltip;
    }, [disabled, disabledText, tooltip]);

    const [open, setOpen] = useState(false);

    // Force close tooltip when expanded state changes or when forceHideTooltip is true
    useEffect(() => {
      if (forceHideTooltip) {
        setOpen(false);
      }
    }, [expanded, forceHideTooltip]);

    return (
      <Tooltip open={expanded ? false : open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <Button
            id={id}
            ref={ref}
            variant="ghost"
            size={expanded ? 'default' : 'icon'}
            className={cn(
              'group/btn relative',
              expanded
                ? 'w-full text-md py-4 justify-start'
                : 'w-full ml-0.5 justify-center',
              disabled && 'opacity-50 cursor-default hover:bg-transparent',
              !disabled && 'hover:bg-primary/5',
              isActive && 'bg-primary/10 hover:bg-primary/10',
            )}
            onClick={disabled ? () => noop : onClick}
          >
            {/* Active indicator bar */}
            {isActive && (
              <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary rounded-full" />
            )}
            <Icon
              className={cn(
                'size-6',
                expanded ? 'mr-2' : '',
                !disabled &&
                  'group-hover/btn:text-primary transition-colors duration-150',
                isActive && 'text-primary',
              )}
            />
            <div
              className={cn(
                'transition-opacity-width overflow-hidden text-base',
                expanded
                  ? 'delay-100 duration-100 ease-in opacity-100'
                  : 'duration-300 opacity-0 w-0',
                isActive && 'font-medium',
              )}
            >
              {text}
            </div>
          </Button>
        </TooltipTrigger>
        {!expanded && (
          <TooltipContent className="ml-4">{tooltipText}</TooltipContent>
        )}
      </Tooltip>
    );
  },
);

interface ReplayButtonProps {
  className?: string;
  variant: 'ghost' | 'outline';
  forceHideTooltip?: boolean;
}

const ReplayButton = ({
  variant,
  className,
  forceHideTooltip = false,
}: ReplayButtonProps) => {
  const { viewMode } = useViewMode();
  const { isPlaying } = useReplayContext();
  const { startReplay, pauseReplay } = useStartStopPauseReplay();
  const { messages } = useLocalization();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (forceHideTooltip) {
      setOpen(false);
    }
  }, [forceHideTooltip]);

  const onPlayPause = useCallback(() => {
    if (isPlaying) {
      pauseReplay();
    } else {
      startReplay();
    }
  }, [isPlaying, pauseReplay, startReplay]);

  if (viewMode !== ViewMode.Replay) {
    return null;
  }

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <Button
          className={cn(className)}
          variant={variant}
          onClick={onPlayPause}
        >
          {!isPlaying && <PiPlay className="size-5" />}
          {isPlaying && <PiPause className="size-5" />}
        </Button>
      </TooltipTrigger>
      {isPlaying && (
        <TooltipContent>{messages.replayPauseTooltip}</TooltipContent>
      )}
    </Tooltip>
  );
};

const ReplayStopButton = ({
  variant,
  className,
  forceHideTooltip = false,
}: ReplayButtonProps) => {
  const { viewMode } = useViewMode();
  const { stopReplay } = useStartStopPauseReplay();
  const { messages } = useLocalization();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (forceHideTooltip) {
      setOpen(false);
    }
  }, [forceHideTooltip]);

  if (viewMode !== ViewMode.Replay) {
    return null;
  }

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <Button
          className={cn(className)}
          variant={variant}
          onClick={stopReplay}
        >
          <PiStopFill className="size-5 text-red-500" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{messages.replayStopTooltip}</TooltipContent>
    </Tooltip>
  );
};

const SidebarReplayButton = forwardRef<HTMLButtonElement, SidebarButtonProps>(
  (
    {
      id,
      icon: Icon,
      disabled,
      disabledText,
      text,
      tooltip,
      onClick,
      isActive,
      expanded,
      forceHideTooltip = false,
    }: SidebarButtonProps,
    ref,
  ) => {
    const { viewMode } = useViewMode();
    const [open, setOpen] = useState(false);

    useEffect(() => {
      if (forceHideTooltip) {
        setOpen(false);
      }
    }, [expanded, forceHideTooltip]);

    const tooltipText = useMemo(() => {
      if (disabled) {
        return disabledText;
      } else return tooltip;
    }, [disabled, disabledText, tooltip]);

    return (
      <Tooltip open={expanded ? false : open} onOpenChange={setOpen}>
        <div
          className={
            expanded
              ? cn(
                  'relative flex items-center rounded-lg',
                  disabled && 'opacity-50 cursor-default',
                )
              : 'relative flex'
          }
        >
          <TooltipTrigger asChild>
            <Button
              id={id}
              variant="ghost"
              size={expanded ? 'default' : 'icon'}
              className={cn(
                'group/btn relative',
                expanded
                  ? 'w-full py-4 text-md justify-start'
                  : 'w-full ml-0.5 justify-center',
                disabled && 'opacity-50 cursor-default hover:bg-transparent',
                !disabled && 'hover:bg-primary/5',
                isActive && 'bg-primary/10 hover:bg-primary/10',
              )}
              onClick={disabled ? () => noop : onClick}
            >
              {/* Active indicator bar */}
              {isActive && (
                <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary rounded-full" />
              )}
              <Icon
                className={cn(
                  'size-6',
                  expanded ? 'mr-2' : '',
                  !disabled &&
                    'group-hover/btn:text-primary transition-colors duration-150',
                  isActive && 'text-primary',
                )}
              />
              <div
                className={cn(
                  'transition-opacity-width overflow-hidden text-base',
                  expanded
                    ? 'delay-100 duration-100 ease-in opacity-100'
                    : 'duration-300 opacity-0 w-0',
                  isActive && 'font-medium',
                )}
              >
                {text}
              </div>
            </Button>
          </TooltipTrigger>
          {expanded ? (
            <div
              className={cn(
                'flex bg-background rounded-lg',
                viewMode === ViewMode.Replay &&
                  'absolute -right-23 mr-2 space-x-1',
              )}
            >
              <ReplayStopButton
                className="h-full w-8 px-2"
                variant="ghost"
                forceHideTooltip={forceHideTooltip}
              ></ReplayStopButton>
              <ReplayButton
                className="h-full w-8 px-2"
                variant="ghost"
                forceHideTooltip={forceHideTooltip}
              ></ReplayButton>
            </div>
          ) : (
            <div
              className={cn(
                'flex rounded-lg',
                viewMode === ViewMode.Replay &&
                  'absolute -right-[72px] bg-background space-x-1',
              )}
            >
              <ReplayStopButton
                className="h-full w-8 px-2"
                variant="ghost"
              ></ReplayStopButton>
              <ReplayButton
                className="h-full w-8 px-2"
                variant="ghost"
              ></ReplayButton>
            </div>
          )}
        </div>
        {disabled && (
          <TooltipContent className="ml-4">{disabledText}</TooltipContent>
        )}
        {!expanded && (
          <TooltipContent className="ml-4">{tooltipText}</TooltipContent>
        )}
      </Tooltip>
    );
  },
);

const SidebarLogo = React.memo(({ expanded }: { expanded: boolean }) => {
  const { whitelabelImagePaths } = useWhitelabel();
  const [useDarkLogo, setUseDarkLogo] = useState(false);

  // Check if dark logo exists and if we're in dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setUseDarkLogo(isDark);
    };

    // Initial check
    checkDarkMode();

    // Watch for dark mode changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  const logoSrc = expanded
    ? useDarkLogo
      ? whitelabelImagePaths.sidebarExpandedDarkPath
      : whitelabelImagePaths.sidebarExpandedPath
    : useDarkLogo
      ? whitelabelImagePaths.sidebarCollapsedDarkPath
      : whitelabelImagePaths.sidebarCollapsedPath;

  const fallbackSrc = expanded
    ? whitelabelImagePaths.sidebarExpandedPath
    : whitelabelImagePaths.sidebarCollapsedPath;

  return (
    <div className={cn(expanded ? 'p-6 pb-0' : 'mx-2.5 pt-6 pb-4')}>
      <img
        src={logoSrc}
        alt="Home logo"
        className={cn('object-center', expanded ? 'w-40' : 'w-11')}
        onError={(e) => {
          // Fallback to light logo if dark logo doesn't exist
          if (useDarkLogo && e.currentTarget.src !== fallbackSrc) {
            e.currentTarget.src = fallbackSrc;
          }
        }}
      />
    </div>
  );
});

export const Sidebar = forwardRef<HTMLDivElement, SidebarProps>(
  ({ id, className, ...props }: SidebarProps, ref) => {
    const { dbSession, replaySession } = useSession();
    const { activeTab } = useMapPanelContext();
    const sidebarAction = useSidebarItemAction();

    const onSidebarItemClick = (buttonId: string) => {
      return trackOperation(
        () => sidebarAction(buttonId),
        `sidebar.click.${buttonId}`,
        'ui.interaction',
        {
          buttonId,
          previousTab: activeTab,
          action: 'sidebar_navigation',
          timestamp: new Date().toISOString(),
          category: 'navigation',
          message: `Clicked sidebar button: ${buttonId}`,
        },
      )();
    };

    const permissions = usePermissions();
    const { messages } = useLocalization();

    const { notifications } = useNotificationContext();
    const { settings } = useSettings();
    const [expanded, setExpanded] = useState(
      settings['ribbonPlugin.settings']?.is_sidebar_expanded ?? true,
    );

    // State to track if tooltips should be forcibly hidden
    const [forceHideTooltips, setForceHideTooltips] = useState(false);

    // When expanded state changes, force hide all tooltips
    const handleExpandToggle = () => {
      setForceHideTooltips(true);
      setExpanded(!expanded);

      // Reset the force hide after a short delay
      setTimeout(() => {
        setForceHideTooltips(false);
      }, 500);
    };

    return (
      <TooltipProvider>
        <div
          id={id}
          ref={ref}
          {...props}
          className={cn(
            'relative h-full',
            expanded ? 'w-52' : 'w-16',
            'pb-12 z-10 transition-[width] duration-300 ease-in-out group/expanded-sidebar bg-gradient-to-b from-background to-muted/30 shadow-[2px_0_12px_rgba(0,0,0,0.06)]',
            className,
          )}
        >
          <div className="h-24">
            <SidebarLogo expanded={expanded} />
          </div>
          <Button
            onClick={handleExpandToggle}
            className="absolute top-16 -right-3 bg-background rounded-full border border-border text-muted-foreground hover:bg-accent h-6 w-6 p-0 opacity-0 group-hover/expanded-sidebar:opacity-100 transition-opacity"
          >
            {expanded ? (
              <PiCaretLeftLight className="size-4" />
            ) : (
              <PiCaretRightLight className="size-4" />
            )}
          </Button>

          <div className={expanded ? 'space-y-2' : ''}>
            <div className={expanded ? 'px-3' : ''}>
              <div className="space-y-2 pt-6">
                <SidebarButton
                  id="sidebar-shapes-button"
                  icon={PiPolygon}
                  text={messages.sidebarShapesText}
                  tooltip={messages.sidebarShapesText}
                  onClick={() => onSidebarItemClick(shapesButtonId)}
                  isActive={activeTab === ACTIVETAB.SHAPES}
                  expanded={expanded}
                  forceHideTooltip={forceHideTooltips}
                />
                <SidebarButton
                  id="sidebar-filters-button"
                  icon={PiFunnel}
                  text={messages.sidebarFiltersText}
                  tooltip={messages.sidebarFiltersText}
                  onClick={() => onSidebarItemClick(filtersButtonId)}
                  isActive={activeTab === ACTIVETAB.FILTERS}
                  expanded={expanded}
                  forceHideTooltip={forceHideTooltips}
                />
                <SidebarButton
                  id="sidebar-reports-button"
                  icon={PiNotebook}
                  disabled={!permissions.reports || !dbSession}
                  disabledText={
                    !permissions.reports
                      ? messages.sidebarReportsMissingPermission
                      : 'Reports down'
                  }
                  text={messages.sidebarReportsText}
                  tooltip={messages.sidebarReportsText}
                  onClick={() => onSidebarItemClick(reportsButtonId)}
                  isActive={activeTab === ACTIVETAB.REPORTS}
                  expanded={expanded}
                  forceHideTooltip={forceHideTooltips}
                />
                <SidebarButton
                  id="sidebar-events-button"
                  icon={TbRefreshAlert}
                  disabled={!permissions.events}
                  disabledText={messages.sidebarEventsMissingPermission}
                  text={messages.sidebarEventsText}
                  tooltip={messages.sidebarEventsText}
                  onClick={() => onSidebarItemClick(eventsButtonId)}
                  isActive={activeTab === ACTIVETAB.EVENTS}
                  expanded={expanded}
                  forceHideTooltip={forceHideTooltips}
                />
                <SidebarReplayButton
                  id="sidebar-replay-button"
                  icon={PiMonitorPlay}
                  disabled={!permissions.replay || !replaySession}
                  disabledText={
                    !permissions.replay
                      ? messages.sidebarReplayMissingPermission
                      : 'Replay down'
                  }
                  text={messages.sidebarReplayText}
                  tooltip={messages.sidebarReplayText}
                  onClick={() => onSidebarItemClick(replayButtonId)}
                  isActive={activeTab === ACTIVETAB.REPLAY}
                  expanded={expanded}
                  forceHideTooltip={forceHideTooltips}
                />
                <SidebarButton
                  id="sidebar-atons-button"
                  icon={PiDiamond}
                  disabled={!permissions.atons}
                  disabledText={messages.sidebarAtonsMissingPermission}
                  text={messages.sidebarAtonsText}
                  tooltip={messages.sidebarAtonsText}
                  onClick={() => onSidebarItemClick(atonsButtonId)}
                  isActive={activeTab === ACTIVETAB.ATONS}
                  expanded={expanded}
                  forceHideTooltip={forceHideTooltips}
                />
                <SidebarButton
                  id="sidebar-messages-button"
                  icon={PiChatCircleText}
                  disabled={!permissions.messages}
                  disabledText={messages.sidebarMessagesMissingPermission}
                  text={messages.sidebarMessagesText}
                  tooltip={messages.sidebarMessagesText}
                  onClick={() => onSidebarItemClick(messagesButtonId)}
                  isActive={activeTab === ACTIVETAB.MESSAGES}
                  expanded={expanded}
                  forceHideTooltip={forceHideTooltips}
                />
              </div>
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-0 w-full">
            <div className="mx-3 mb-2 border-t border-border/50" />
            <UserDropdown expanded={expanded}></UserDropdown>
          </div>
        </div>
      </TooltipProvider>
    );
  },
);
